import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

/* ---------- Perfil ---------- */

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("profiles")
      .select(
        "id, full_name, avatar_url, theme_preference, live_notification_pref, created_at, approved, cpf, birth_date, pref_default_turno, pref_items_per_page, pref_autoplay, pref_hide_watched, pref_resume_playback, email_sync_status",
      )
      .eq("id", context.userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return data;
    // O banco guarda apenas o caminho no bucket privado; a URL assinada é
    // gerada aqui, com validade curta, a cada leitura.
    const { signAvatarUrl, toAvatarPath } = await import("./avatar.server");
    return {
      ...data,
      avatar_path: toAvatarPath(data.avatar_url),
      avatar_url: await signAvatarUrl(data.avatar_url),
    };
  });

/* ---------- Preferências pessoais de uso ---------- */

const preferencesSchema = z.object({
  pref_default_turno: z.enum(["todos", "manha", "tarde"]).optional(),
  pref_items_per_page: z.union([z.literal(12), z.literal(24), z.literal(48)]).optional(),
  pref_autoplay: z.boolean().optional(),
  pref_hide_watched: z.boolean().optional(),
  pref_resume_playback: z.boolean().optional(),
  theme_preference: z.enum(["light", "dark", "system"]).optional(),
  live_notification_pref: z.enum(["off", "shabbat", "all"]).optional(),
});

export type MyPreferences = z.infer<typeof preferencesSchema>;

/** Salva as preferências pessoais de uso do próprio usuário. */
export const updateMyPreferences = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: MyPreferences) => preferencesSchema.parse(data))
  .handler(async ({ data, context }) => {
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) if (v !== undefined) patch[k] = v;
    if (Object.keys(patch).length === 0) return { ok: true };
    const { error } = await context.supabase
      .from("profiles")
      .update(patch as never)
      .eq("id", context.userId);

    if (error) throw error;
    return { ok: true };
  });

const liveNotifSchema = z.object({ pref: z.enum(["off", "shabbat", "all"]) });
export const updateLiveNotificationPref = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { pref: "off" | "shabbat" | "all" }) => liveNotifSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({ live_notification_pref: data.pref })
      .eq("id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

// Dados pessoais (nome, CPF, data de nascimento e foto) são somente leitura
// aqui: a edição acontece na Gestão de cadastros e chega pela sincronização.
const profileSchema = z.object({
  theme_preference: z.enum(["light", "dark", "system"]).optional(),
});

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: z.infer<typeof profileSchema>) => profileSchema.parse(data))
  .handler(async ({ data, context }) => {
    const patch = {
      ...(data.theme_preference ? { theme_preference: data.theme_preference } : {}),
    };
    if (Object.keys(patch).length === 0) return { ok: true };

    const { error } = await context.supabase
      .from("profiles")
      .update(patch)
      .eq("id", context.userId);
    if (error) throw error;

    return { ok: true };
  });

/* ---------- Dados obrigatórios: leitura a partir da Gestão ---------- */

/**
 * Rebusca os dados pessoais do próprio usuário na Gestão de cadastros e
 * aplica o resultado no perfil local. Não recebe nenhum dado do cliente:
 * nada de nome, CPF ou nascimento pode ser alterado por esta plataforma.
 */
export const refreshMyDataFromGestao = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: authUser } = await supabaseAdmin.auth.admin.getUserById(context.userId);
    const email = (authUser?.user?.email ?? "").trim().toLowerCase();
    if (!email) throw new Error("Não foi possível identificar seu e-mail de acesso.");

    const { syncMemberByEmail } = await import("./member-sync.server");
    const result = await syncMemberByEmail(email);
    if (!result.found) {
      return {
        ok: false,
        found: false,
        message:
          "Seu cadastro ainda não foi localizado na Gestão de cadastros. Complete ou revise seus dados lá e tente novamente.",
      };
    }

    return { ok: true, found: true, blockReason: result.blockReason };
  });

const themeSchema = z.object({ theme: z.enum(["light", "dark", "system"]) });
export const updateMyTheme = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { theme: "light" | "dark" | "system" }) => themeSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({ theme_preference: data.theme })
      .eq("id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

/* ---------- Favoritos ---------- */

const teachingIdSchema = z.object({ teachingId: z.string().uuid() });

export const toggleFavorite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { teachingId: string }) => teachingIdSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase
      .from("user_favorites")
      .select("teaching_id")
      .eq("user_id", context.userId)
      .eq("teaching_id", data.teachingId)
      .maybeSingle();

    if (existing) {
      const { error } = await context.supabase
        .from("user_favorites")
        .delete()
        .eq("user_id", context.userId)
        .eq("teaching_id", data.teachingId);
      if (error) throw error;
      return { favorited: false };
    } else {
      const { error } = await context.supabase
        .from("user_favorites")
        .insert({ user_id: context.userId, teaching_id: data.teachingId });
      if (error) throw error;
      return { favorited: true };
    }
  });

export const listMyFavorites = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_favorites")
      .select(
        "teaching_id, created_at, teachings(id, title, thumbnail_url, teaching_date, turno, hebrew_date, youtube_video_id, parashiot(name_translit, number))",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: any) => r.teachings).filter(Boolean);
  });

export const listMyFavoriteIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_favorites")
      .select("teaching_id")
      .eq("user_id", context.userId);
    return (data ?? []).map((r: any) => r.teaching_id as string);
  });

/* ---------- Progresso / Continuar assistindo ---------- */

const progressSchema = z.object({
  teachingId: z.string().uuid(),
  position: z
    .number()
    .int()
    .min(0)
    .max(60 * 60 * 24),
  duration: z
    .number()
    .int()
    .min(0)
    .max(60 * 60 * 24)
    .optional(),
});

export const upsertWatchProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { teachingId: string; position: number; duration?: number }) =>
    progressSchema.parse(data),
  )
  .handler(async ({ data, context }) => {
    const completed = data.duration ? data.position >= Math.max(0, data.duration - 30) : false;
    const { error } = await context.supabase.from("user_watch_progress").upsert(
      {
        user_id: context.userId,
        teaching_id: data.teachingId,
        position_seconds: data.position,
        duration_seconds: data.duration ?? null,
        completed,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,teaching_id" },
    );
    if (error) throw error;
    return { ok: true };
  });

export const listContinueWatching = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_watch_progress")
      .select(
        "teaching_id, position_seconds, duration_seconds, completed, updated_at, teachings(id, title, thumbnail_url, teaching_date, turno, hebrew_date, youtube_video_id, parashiot(name_translit, number))",
      )
      .eq("user_id", context.userId)
      .eq("completed", false)
      .order("updated_at", { ascending: false })
      .limit(8);
    if (error) throw error;
    return (data ?? [])
      .filter((r: any) => r.teachings)
      .map((r: any) => ({
        ...r.teachings,
        _progress: r.position_seconds,
        _duration: r.duration_seconds,
      }));
  });

export const listRecentlyWatched = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_watch_progress")
      .select(
        "teaching_id, position_seconds, duration_seconds, completed, updated_at, teachings(id, title, thumbnail_url, teaching_date, turno, hebrew_date, youtube_video_id, parashiot(name_translit, number))",
      )
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false })
      .limit(5);
    if (error) throw error;
    return (data ?? [])
      .filter((r: any) => r.teachings)
      .map((r: any) => ({
        ...r.teachings,
        _progress: r.position_seconds,
        _duration: r.duration_seconds,
      }));
  });

export const getMyWatchHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_watch_progress")
      .select(
        "teaching_id, position_seconds, duration_seconds, completed, updated_at, teachings(id, title, thumbnail_url, teaching_date, turno, hebrew_date, youtube_video_id, parashiot(name_translit, number))",
      )
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return (data ?? [])
      .filter((r: any) => r.teachings)
      .map((r: any) => ({
        ...r.teachings,
        _progress: r.position_seconds,
        _duration: r.duration_seconds,
        _completed: r.completed,
        _watchedAt: r.updated_at,
      }));
  });

/** IDs dos ensinos já concluídos pelo usuário (usado na preferência "ocultar assistidos"). */
export const listCompletedTeachingIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_watch_progress")
      .select("teaching_id")
      .eq("user_id", context.userId)
      .eq("completed", true);
    if (error) throw error;
    return (data ?? []).map((r) => r.teaching_id as string);
  });

export const getWatchProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { teachingId: string }) => teachingIdSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { data: row } = await context.supabase
      .from("user_watch_progress")
      .select("position_seconds, duration_seconds")
      .eq("user_id", context.userId)
      .eq("teaching_id", data.teachingId)
      .maybeSingle();
    return row;
  });
